import type { DocsContent } from "./types";

const ko = {
  locale: "ko",
  shell: {
    navigationLabel: "문서 탐색",
    browse: "문서 둘러보기",
    title: "Comfort 가이드",
    description: "원칙부터 파운데이션까지 차례로 살펴보세요.",
    readDesign: "DESIGN.md 읽기",
    paginationLabel: "이전·다음 문서",
    previous: "이전",
    next: "다음",
    reviewed: "마지막 확인 · 2026년 8월",
    outlineLabel: "현재 페이지 목차",
    outlineTitle: "이 페이지에서",
    overviewGroup: "시작하기",
    sections: {
      principles: "원칙",
      foundations: "파운데이션",
    },
  },
  search: {
    ariaLabel: "목록 검색",
    label: "이름으로 찾아보세요",
    placeholder: "이름이나 쓰임새를 입력하세요",
    reset: "검색 초기화",
    result: "개 결과",
    results: "개 결과",
  },
  principles: {
    eyebrow: "디자인 원칙",
    title: "편안한 제품을 만드는 네 가지 기준",
    description:
      "Comfort가 말하는 편안함은 부드러운 인상에 그치지 않아요. 다음 행동을 쉽게 알아보고, 결과를 예상하며, 필요한 만큼 자연스럽게 익힐 수 있어야 해요.",
    outlineModel: "한눈에 보기",
    outlineReview: "출시 전 확인",
    intent: "왜 필요한가요?",
    practice: "이렇게 적용해요",
    modelLabel: "네 가지 Comfort 원칙이 이어지는 흐름",
    modelCenter: "명확함에서 오는 신뢰",
    reviewBadge: "출시 전 점검",
    reviewTitle: "새 패턴을 내보내기 전에 확인하세요",
    reviewItems: [
      "색에 기대지 않아도 문구와 위치, 상태만으로 다음 행동을 알 수 있어요.",
      "비슷한 컴포넌트와 같은 방식으로 동작하고 시맨틱 토큰을 사용해요.",
      "피드백이 행동한 곳 가까이에 나타나고 다음 선택지를 알려줘요.",
      "익숙한 사용법을 바꾸지 않으면서 고급 기능을 단계적으로 보여줄 수 있어요.",
    ],
    items: [
      {
        id: "natural",
        number: "01",
        title: "자연스럽게",
        comfortTitle: "설명하지 않아도 다음 행동이 보여야 해요",
        summary:
          "사람이 이미 익숙한 표현과 움직임을 따릅니다. 생각하거나 조작하는 데 드는 부담을 줄여요.",
        practice:
          "쉬운 문장을 쓰고, 화면 안의 위치 관계를 유지하며, 주 행동은 시선이 머무는 곳에 둡니다.",
        question: "설명 없이 보아도 무엇을 해야 할지 알 수 있나요?",
      },
      {
        id: "certain",
        number: "02",
        title: "예측 가능하게",
        comfortTitle: "같은 상황에는 같은 방식으로 답해요",
        summary:
          "반복되는 규칙과 꼭 필요한 선택지만 남기면 사용자는 결과를 걱정하지 않고 행동할 수 있어요.",
        practice:
          "시맨틱 토큰을 사용하고, 비슷한 컴포넌트는 같은 상태와 피드백을 보여줍니다.",
        question: "누르기 전에 어떤 일이 생길지 예상할 수 있나요?",
      },
      {
        id: "meaningful",
        number: "03",
        title: "목적에 맞게",
        comfortTitle: "중요한 일에 시선을 모아요",
        summary:
          "시각 요소와 인터랙션은 목적을 분명히 하고, 진행 상태를 보여주며, 막혔을 때 돌아갈 길을 알려줘야 해요.",
        practice:
          "핵심 과업을 중심에 두고, 피드백은 행동한 곳 가까이에 보여주며, 결정을 방해하는 장식은 덜어냅니다.",
        question:
          "이 요소가 일을 끝내거나 현재 상태를 이해하는 데 도움이 되나요?",
      },
      {
        id: "growing",
        number: "04",
        title: "함께 자라게",
        comfortTitle: "처음에는 쉽고, 익숙해지면 더 깊게",
        summary:
          "처음 만난 사람에게도 유용하고, 쓰면서 더 많은 기능을 발견하며, 예상 밖의 변화 없이 확장할 수 있어야 해요.",
        practice:
          "고급 기능은 단계적으로 보여주고, 이미 익힌 사용법은 지키며, 피드백을 다음 개선에 반영합니다.",
        question: "기능이 늘어나도 처음 배운 사용법을 그대로 쓸 수 있나요?",
      },
    ],
  },
  foundations: {
    eyebrow: "파운데이션",
    title: "화면마다 반복되는 기준을 한곳에 모았어요",
    description:
      "색, 글자, 간격, 모션처럼 여러 화면에서 함께 쓰는 기준입니다. 먼저 역할을 정하고, 필요한 만큼 조합해 보세요.",
    outlineCatalog: "파운데이션 찾기",
    outlineDirectory: "전체 목록",
    outlineLayers: "적용 순서",
    featuredNote: "예시와 적용 방법",
    emptyTitle: "찾는 파운데이션이 없어요",
    emptyDescription: "더 짧은 이름이나 넓은 쓰임새로 검색해 보세요.",
    directoryEyebrow: "파운데이션 15개",
    directoryTitle: "전체 목록",
    layersBadge: "적용 순서",
    layersTitle: "의도에서 실제 화면까지",
    layersDescription:
      "앞 단계에서 정한 기준이 다음 단계의 선택을 더 쉽게 만들어요.",
    layers: [
      ["01", "원칙", "왜 이런 방식으로 동작해야 하는지 정해요"],
      ["02", "시맨틱 토큰", "테마가 달라도 유지할 역할을 정해요"],
      ["03", "컴포넌트", "역할을 반복해서 쓸 수 있는 UI로 만들어요"],
    ],
  },
  foundationDetail: {
    eyebrow: "파운데이션",
    overview: "미리보기",
    intent: "쓰임새",
    guidelines: "적용 방법",
    accessibility: "접근성",
    reference: "참고 값",
    intentEyebrow: "이 기준이 필요한 이유",
    intentTitle: "무엇을 일관되게 만드는지 확인하세요",
    guidanceBadge: "핵심 가이드",
    guidanceTitle: "제품에 적용할 때 살펴볼 점",
    accessibilityEyebrow: "누구나 사용할 수 있게",
    accessibilityTitle: "접근성 확인 항목",
    referenceTitle: "참고 값",
    referenceDescription:
      "역할 이름은 유지하고 실제 값은 테마, 화면 너비, 언어, 인터랙션 상태에 맞게 바꿀 수 있어요.",
    role: "역할",
    use: "쓰임새",
    value: "참고 값",
    sourceTitle: "DESIGN.md에서 더 자세히",
    sourceDescription:
      "실제 제품에 적용할 값과 확인 항목은 DESIGN.md에서 관리합니다. 이 페이지에서는 결과를 눈으로 확인할 수 있어요.",
    overviewDescription: "파운데이션 목록",
  },
  notFound: {
    title: "페이지를 찾을 수 없어요",
    description: "홈으로 돌아가 원칙이나 파운데이션에서 다시 찾아보세요.",
    action: "Comfort 홈으로",
  },
  foundationCopy: {
    "design-token": {
      description:
        "테마와 제품이 달라져도 결정을 그대로 옮길 수 있는 시맨틱 어휘예요.",
      intent:
        "모양보다 목적을 먼저 정의해, 의미를 바꾸지 않고도 컴포넌트가 적응할 수 있게 해요.",
      rules: [
        "의도가 드러나도록 역할 이름을 지으세요.",
        "별칭은 하나의 원본을 거쳐 연결하세요.",
        "라이트와 다크 역할을 나란히 유지하세요.",
      ],
      uses: ["행동과 포커스", "페이지 바탕", "콘텐츠 표면"],
      accessibility: [
        "토큰이 바뀌어도 대비 기준을 유지해요.",
        "상태 역할을 색에만 맡기지 않아요.",
      ],
    },
    color: {
      description:
        "차분한 뉴트럴이 조용한 구조를 만들고, 하나의 파란색이 인터랙션을 알려요.",
      intent: "색을 장식으로 쓰지 않으면서 위계와 행동, 상태를 분명하게 해요.",
      rules: [
        "파란색은 인터랙션에만 사용하세요.",
        "시맨틱 상태 색은 그대로 유지하세요.",
        "두 테마 모두에서 확인하세요.",
      ],
      uses: ["라이트 테마의 행동", "다크 테마의 행동", "본문 콘텐츠"],
      accessibility: [
        "본문 텍스트는 4.5 대 1 대비를 지켜요.",
        "텍스트가 아닌 컨트롤은 3 대 1을 지켜요.",
      ],
    },
    typography: {
      description:
        "제품 콘텐츠와 보조 텍스트, 코드를 위한 간결한 Geist 스케일이에요.",
      intent:
        "표면을 더하기 전에 크기, 굵기, 글줄 길이, 간격으로 위계를 전달해요.",
      rules: [
        "문장형 표기를 사용하세요.",
        "제목은 균형 있게, 본문은 자연스럽게 줄바꿈하세요.",
        "모노스페이스는 기능적인 데이터에만 사용하세요.",
      ],
      uses: ["히어로 문장", "제품 본문", "메타데이터"],
      accessibility: [
        "200% 확대에서도 텍스트가 다시 흐르도록 해요.",
        "CJK에서는 의미 단위 어절을 유지해요.",
      ],
    },
    iconography: {
      description:
        "일관된 기하학 아이콘이 레이블을 대신하지 않으면서 훑어보기를 도와요.",
      intent:
        "하나의 시각 문법을 유지하고, 익숙하지 않은 아이콘은 텍스트가 의미를 전달하게 해요.",
      rules: [
        "획 굵기를 일정하게 유지하세요.",
        "시각적 정렬을 사용하세요.",
        "진부한 은유는 피하세요.",
      ],
      uses: ["인라인 보조", "표준 컨트롤", "일러스트 용도"],
      accessibility: [
        "장식 아이콘은 보조 기술에서 숨겨요.",
        "아이콘만 있는 컨트롤에는 접근 가능한 이름을 붙여요.",
      ],
    },
    elevation: {
      description: "표면 대비와 절제된 그림자가 실제 쌓임 관계를 설명해요.",
      intent: "모든 영역을 띄우지 않으면서 담김과 겹침을 보여줘요.",
      rules: [
        "그림자보다 톤을 먼저 사용하세요.",
        "그림자를 쌓임 순서와 맞추세요.",
        "조명 방향은 하나로 유지하세요.",
      ],
      uses: ["페이지 콘텐츠", "카드와 바", "메뉴와 팝오버", "다이얼로그"],
      accessibility: [
        "선택 상태를 엘리베이션에만 맡기지 않아요.",
        "고대비 모드에서도 경계가 유지돼요.",
      ],
    },
    gradient: {
      description:
        "통제된 램프는 데이터와 마스크, 정해진 히어로 텍스트에만 사용해요.",
      intent: "연속성 자체가 정보를 전달할 때만 연속된 색을 사용해요.",
      rules: [
        "인터페이스 배경은 평평하게 유지하세요.",
        "데이터 램프에는 레이블을 붙이세요.",
        "장식적인 AI 그라데이션은 피하세요.",
      ],
      uses: ["크기의 순서", "가장자리 연속", "제목 강조"],
      accessibility: [
        "램프 양 끝이 대비 기준을 충족해요.",
        "데이터에는 색이 아닌 대안을 함께 제공해요.",
      ],
    },
    "inclusive-design": {
      description:
        "시맨틱과 대비, 포커스, 너그러운 인터랙션이 모든 컴포넌트의 기본이에요.",
      intent:
        "지각, 입력, 확대, 모션 선호가 달라도 핵심 과업을 수행할 수 있게 해요.",
      rules: [
        "네이티브 시맨틱을 우선하세요.",
        "포커스를 항상 보이게 유지하세요.",
        "실패 지점 옆에 복구 경로를 두세요.",
      ],
      uses: ["터치 타깃", "보이는 포커스 링", "최소 너비"],
      accessibility: [
        "WCAG 2.2 AA가 기본선이에요.",
        "보조 기술도 같은 과업 순서를 따라요.",
      ],
    },
    "international-design": {
      description:
        "번역과 글쓰기 방향, 로케일별 형식이 달라져도 레이아웃과 콘텐츠가 유지돼요.",
      intent:
        "언어 확장과 방향 전환을 예외가 아니라 자연스러운 제품 상태로 다뤄요.",
      rules: [
        "문구가 30% 늘어날 여유를 두세요.",
        "논리적 속성을 사용하세요.",
        "날짜와 숫자는 로케일에 맞게 표기하세요.",
      ],
      uses: ["문구 여유 공간", "레이아웃 논리", "형식 표기"],
      accessibility: [
        "언어와 방향 속성을 정확하게 지정해요.",
        "번역된 레이블이 잘리지 않아요.",
      ],
    },
    layout: {
      description:
        "반응형 컨테이너와 안정된 읽기 순서가 과업의 우선순위를 지켜요.",
      intent:
        "의미를 바꾸거나 핵심 행동을 예측할 수 없게 옮기지 않으면서 밀도와 칼럼을 조절해요.",
      rules: [
        "소스 순서에서 시작하세요.",
        "다축 레이아웃에는 그리드를 사용하세요.",
        "읽기 좋은 글줄 길이를 넘지 마세요.",
      ],
      uses: ["컴팩트한 페이지", "읽기 글줄", "최대 셸"],
      accessibility: [
        "320px 너비에서도 페이지가 넘치지 않아요.",
        "확대해도 과업 순서가 유지돼요.",
      ],
    },
    motion: {
      description: "원인과 방향, 연속성이 상태 변화를 이해하기 쉽게 만들어요.",
      intent:
        "입력을 지연시키거나 장식적인 이동을 더하지 않고 무엇이 바뀌었는지 설명해요.",
      rules: [
        "100ms 안에 피드백을 시작하세요.",
        "transform과 opacity만 애니메이션하세요.",
        "동작 줄이기에서도 같은 정보를 제공하세요.",
      ],
      uses: ["누름 피드백", "상태 변화", "패널 이동"],
      accessibility: [
        "동작 줄이기에서는 이동을 제거해요.",
        "애니메이션 없이도 상태가 보여요.",
      ],
    },
    radius: {
      description:
        "절제된 모서리 스케일이 컨테이너를 부드럽지만 정확하게 유지해요.",
      intent:
        "모든 컨트롤을 알약 모양으로 만들지 않으면서 담김과 중첩을 표현해요.",
      rules: [
        "큰 표면 안에는 더 작은 반경을 사용하세요.",
        "알약 모양은 상태 표시에만 남겨두세요.",
        "이음새는 직각으로 유지하세요.",
      ],
      uses: ["태그와 조각", "컨트롤", "카드", "다이얼로그"],
      accessibility: [
        "모양이 유일한 상태 단서가 되지 않아요.",
        "포커스 링이 보이는 윤곽을 따라가요.",
      ],
    },
    spacing: {
      description: "4픽셀 스케일이 결정을 구분하고 관련 콘텐츠를 묶어요.",
      intent:
        "테두리와 카드, 장식적인 채움을 더하기 전에 간격의 리듬으로 위계를 만들어요.",
      rules: [
        "문서화된 단계만 사용하세요.",
        "형제 요소 사이에는 마진보다 gap을 사용하세요.",
        "섹션 리듬은 시각적으로 조정하세요.",
      ],
      uses: ["촘촘한 레이블", "컨트롤 내부", "관련 콘텐츠", "큰 구분"],
      accessibility: [
        "터치 타깃 사이 간격을 충분히 유지해요.",
        "텍스트가 늘어나도 간격이 무너지지 않아요.",
      ],
    },
    state: {
      description:
        "공통 라이프사이클이 대기, 사용 가능, 비어 있음, 복구 가능을 구분해요.",
      intent:
        "지금 무슨 일이 일어나는지, 무엇이 안전한지, 어떤 행동으로 복구할 수 있는지 보여줘요.",
      rules: [
        "새로 고치는 동안 이전 데이터를 유지하세요.",
        "스켈레톤은 크기를 아는 영역에만 사용하세요.",
        "오류는 복구 수단 가까이에 두세요.",
      ],
      uses: ["사용할 데이터 없음", "이전 데이터 유지", "복구 가능"],
      accessibility: [
        "상태 변화를 적절한 알림으로 전달해요.",
        "의도 없이 포커스가 이동하지 않아요.",
      ],
    },
    "voice-and-tone": {
      description:
        "직접적이고 차분한 언어가 위험도와 맥락에 따라 강조를 조절해요.",
      intent: "모호한 들뜸이나 탓하기 없이 결정하고 복구할 수 있게 도와요.",
      rules: [
        "결과부터 말하세요.",
        "대상과 결과를 구체적으로 부르세요.",
        "긴급함은 실제 위험에 맞추세요.",
      ],
      uses: ["일상적인 안내", "확정된 결과", "복구 안내"],
      accessibility: [
        "관용구를 피해요.",
        "긴급한 문구도 문장 부호 없이 이해돼요.",
      ],
    },
    writing: {
      description:
        "구체적인 인터페이스 문구가 행동과 결과, 상태, 복구 경로를 그대로 불러요.",
      intent: "다음 결정을 쉬운 능동형 문장으로 적어 해석의 여지를 줄여요.",
      rules: [
        "능동태를 사용하세요.",
        "행동에는 동사와 결과를 담아 이름 붙이세요.",
        "오류 복구는 그 자리에서 안내하세요.",
      ],
      uses: ["행동 레이블", "복구 문구", "다음 행동"],
      accessibility: [
        "레이블이 화면 밖에서도 이해돼요.",
        "번역할 때 문장 조각을 이어 붙이지 않아요.",
      ],
    },
  },
} satisfies DocsContent;

export default ko;
